import isURL from 'validator/lib/isURL';
import extractUser from './extractUser';
import isValidDomain from './isValidDomain';

/** Editable profile form fields (X uses the `twitter` key). */
export type ProfileSocialField =
  | 'facebook'
  | 'instagram'
  | 'soundcloud'
  | 'twitter'
  | 'youtube'
  | 'spotify'
  | 'appleMusic';

const YOUTUBE_CHANNEL_ID = /^(UC|HC)[A-Za-z0-9_-]{22}$/;

const SIMPLE_HANDLE = /^[A-Za-z0-9._-]+$/;
const X_HANDLE = /^[A-Za-z0-9_]+$/;

export function applyHostAliases(hostname: string): string {
  let h = hostname.toLowerCase();
  if (h.startsWith('www.')) h = h.slice(4);
  const aliases: Record<string, string> = {
    'm.facebook.com': 'facebook.com',
    'm.instagram.com': 'instagram.com',
    'm.youtube.com': 'youtube.com',
    'm.soundcloud.com': 'soundcloud.com',
  };
  return aliases[h] ?? h;
}

/**
 * Parses common pasted profile URLs, applies v1 host aliases, forces https,
 * strips query string and fragment.
 */
export function canonicalizeSocialUrlString(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const withProto = /^https?:\/\//i.test(t) ? t : `https://${t.replace(/^\/\//, '')}`;
  try {
    const u = new URL(withProto);
    u.protocol = 'https:';
    const host = applyHostAliases(u.hostname);
    u.hostname = host;
    u.search = '';
    u.hash = '';
    let path = u.pathname;
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
    u.pathname = path;
    return u.toString();
  } catch {
    return null;
  }
}

export function canonicalizeStoredLink(link: string): string {
  const c = canonicalizeSocialUrlString(link);
  return c ?? link.trim();
}

/** Resolves stored YouTube text to an absolute URL for pathname parsing (not re-exported from package index). */
export function youtubeDisplayAbsoluteUrl(c: string): URL {
  return new URL(c.startsWith('http') ? c : `https://${c}`);
}

export function inputContainsTwitterHost(raw: string): boolean {
  const lower = raw.toLowerCase();
  return /\btwitter\.com\b/.test(lower) || /\bmobile\.twitter\.com\b/.test(lower);
}

function looksLikeWebAddress(s: string): boolean {
  return /:\/\//.test(s) || /^www\./i.test(s) || /\.(com|net|org|io|app)\b/i.test(s);
}

function normalizeSimpleHandleField(
  domain: 'facebook' | 'instagram' | 'soundcloud',
  value: string,
): { ok: boolean; canonical: string } {
  let username: string;
  if (looksLikeWebAddress(value)) {
    const c = canonicalizeSocialUrlString(value);
    if (!c) return { ok: false, canonical: '' };
    const u = new URL(c);
    const host = applyHostAliases(u.hostname);
    if (host !== `${domain}.com`) return { ok: false, canonical: '' };
    const path = u.pathname.replace(/^\/+|\/+$/g, '');
    if (!path || path.includes('/')) return { ok: false, canonical: '' };
    username = path;
  } else {
    username = value.replace(/^@/, '').trim();
  }
  if (!username || !SIMPLE_HANDLE.test(username)) return { ok: false, canonical: '' };
  return { ok: true, canonical: `https://${domain}.com/${username}` };
}

function normalizeXProfileInput(value: string): { ok: boolean; canonical: string } {
  if (inputContainsTwitterHost(value)) return { ok: false, canonical: '' };
  const v = value;

  if (isURL(v, { require_protocol: false }) || /^(https?:)?\/\//i.test(v)) {
    const c = canonicalizeSocialUrlString(v);
    if (!c) return { ok: false, canonical: '' };
    const u = new URL(c);
    const host = applyHostAliases(u.hostname);
    if (host !== 'x.com') return { ok: false, canonical: '' };
    const path = u.pathname.replace(/^\/+|\/+$/g, '');
    if (!path || path.includes('/')) return { ok: false, canonical: '' };
    if (!X_HANDLE.test(path)) return { ok: false, canonical: '' };
    return { ok: true, canonical: `https://x.com/${path}` };
  }

  const withoutAt = v.replace(/^@/, '');
  if (!withoutAt || !X_HANDLE.test(withoutAt)) return { ok: false, canonical: '' };
  return { ok: true, canonical: `https://x.com/${withoutAt}` };
}

function normalizeYoutubeProfileInput(value: string): { ok: boolean; canonical: string } {
  const v = value;

  const asUrl = looksLikeWebAddress(v);
  if (asUrl) {
    const c = canonicalizeSocialUrlString(v);
    if (!c) return { ok: false, canonical: '' };
    const u = new URL(c);
    const host = applyHostAliases(u.hostname);
    if (host !== 'youtube.com') return { ok: false, canonical: '' };
    const p = u.pathname;
    if (/\/c\//i.test(p)) return { ok: false, canonical: '' };
    if (/\/user\//i.test(p)) return { ok: false, canonical: '' };
    const channel = p.match(/^\/channel\/((?:UC|HC)[A-Za-z0-9_-]{22})\/?$/i);
    if (channel) {
      return { ok: true, canonical: `https://youtube.com/channel/${channel[1]}` };
    }
    const at = p.match(/^\/@([^/]+)\/?$/);
    if (at) {
      const handle = at[1];
      if (!handle || YOUTUBE_CHANNEL_ID.test(handle)) return { ok: false, canonical: '' };
      return { ok: true, canonical: `https://youtube.com/@${handle}` };
    }
    return { ok: false, canonical: '' };
  }

  const bare = v.replace(/^@/, '');
  if (YOUTUBE_CHANNEL_ID.test(bare)) {
    return { ok: true, canonical: `https://youtube.com/channel/${bare}` };
  }
  if (!bare || !SIMPLE_HANDLE.test(bare)) return { ok: false, canonical: '' };
  return { ok: true, canonical: `https://youtube.com/@${bare}` };
}

function normalizeSpotifyArtistInput(value: string): { ok: boolean; canonical: string } {
  const v = value;

  if (looksLikeWebAddress(v)) {
    const c = canonicalizeSocialUrlString(v);
    if (!c) return { ok: false, canonical: '' };
    const u = new URL(c);
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    if (host !== 'open.spotify.com' && host !== 'play.spotify.com') {
      return { ok: false, canonical: '' };
    }
    const m = u.pathname.match(/^\/artist\/([A-Za-z0-9]{10,30})\/?$/i);
    if (!m) return { ok: false, canonical: '' };
    return { ok: true, canonical: `https://open.spotify.com/artist/${m[1]}` };
  }

  if (!/^[A-Za-z0-9]{10,30}$/.test(v)) return { ok: false, canonical: '' };
  return { ok: true, canonical: `https://open.spotify.com/artist/${v}` };
}

function normalizeAppleMusicInput(value: string): { ok: boolean; canonical: string } {
  const v = value;
  if (!looksLikeWebAddress(v)) return { ok: false, canonical: '' };
  const c = canonicalizeSocialUrlString(v);
  if (!c) return { ok: false, canonical: '' };
  const u = new URL(c);
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  if (host !== 'music.apple.com' && host !== 'itunes.apple.com')
    return { ok: false, canonical: '' };
  const path = u.pathname;
  if (!/\/artist\//i.test(path)) return { ok: false, canonical: '' };
  if (!/\/\d+\/?$/.test(path)) return { ok: false, canonical: '' };
  if (host === 'itunes.apple.com') {
    u.hostname = 'music.apple.com';
  }
  return { ok: true, canonical: u.toString() };
}

export function normalizeProfileFieldInput(
  field: ProfileSocialField,
  value: string,
): { ok: boolean; canonical: string } {
  const v = value.trim();
  if (!v) return { ok: true, canonical: '' };

  switch (field) {
    case 'facebook':
      return normalizeSimpleHandleField('facebook', v);
    case 'instagram':
      return normalizeSimpleHandleField('instagram', v);
    case 'soundcloud':
      return normalizeSimpleHandleField('soundcloud', v);
    case 'twitter':
      return normalizeXProfileInput(v);
    case 'youtube':
      return normalizeYoutubeProfileInput(v);
    case 'spotify':
      return normalizeSpotifyArtistInput(v);
    case 'appleMusic':
      return normalizeAppleMusicInput(v);
    default:
      return { ok: false, canonical: '' };
  }
}

export function isProfileFieldInputValid(
  field: ProfileSocialField,
  value: string,
): boolean {
  if (!value.trim()) return true;
  return normalizeProfileFieldInput(field, value).ok;
}

/** Stored link → value shown in the profile text field after load. */
export function displayValueFromStoredLink(
  field: ProfileSocialField,
  link: string,
): string {
  const v = link.trim();
  if (!v) return '';

  switch (field) {
    case 'appleMusic':
      return canonicalizeStoredLink(v);
    case 'youtube': {
      const c = canonicalizeSocialUrlString(v) ?? v;
      try {
        const u = youtubeDisplayAbsoluteUrl(c);
        const ch = u.pathname.match(/^\/channel\/((?:UC|HC)[A-Za-z0-9_-]{22})\/?$/i);
        if (ch) return ch[1];
        const at = u.pathname.match(/^\/@([^/]+)\/?$/);
        if (at) return at[1];
      } catch {
        /* fall through */
      }
      return extractUser(v) || v;
    }
    case 'spotify': {
      const m = v.match(/\/artist\/([A-Za-z0-9]{10,30})\/?$/i);
      return m ? m[1] : v.replace(/^@/, '');
    }
    case 'twitter':
      return extractUser(v) || v.replace(/^@/, '');
    default:
      return extractUser(v) || v.replace(/^@/, '');
  }
}

/** Live helper text target URL while editing (non-empty). */
export function previewHrefForProfileField(
  field: ProfileSocialField,
  value: string,
): string {
  const r = normalizeProfileFieldInput(field, value);
  if (r.ok) return r.canonical;
  if (field === 'youtube') return value.trim();
  return '';
}

/**
 * Icon / public link: legacy twitter.com opens on x.com; otherwise canonicalize.
 */
export function publicHrefForProfileLink(type: string, link: string): string {
  const v = link.trim();
  if (!v) return v;
  if (type === 'twitter' || type === 'x') {
    if (inputContainsTwitterHost(v)) {
      const user = extractUser(v);
      if (user) return `https://x.com/${user}`;
    }
    return canonicalizeStoredLink(v);
  }
  return canonicalizeStoredLink(v);
}

export function isValidProfileFieldUrl(
  field: ProfileSocialField,
  value: string,
): boolean {
  if (!value.trim()) return true;
  const r = normalizeProfileFieldInput(field, value);
  if (!r.ok) return false;
  if (field === 'appleMusic') return true;
  return isValidDomain(r.canonical);
}
